import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';

import Button from '@components/Shared/Buttons/Button';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';

import Sidebar from '@components/DreamToReality/InteractiveFloorplan/Sidebar';
import Drawer from '@components/DreamToReality/InteractiveFloorplan/Drawer';
import ZoomControls from '@components/DreamToReality/InteractiveFloorplan/ZoomControls';
import Floorplan from '@components/DreamToReality/InteractiveFloorplan/Floorplan';

import data from '@components/DreamToReality/InteractiveFloorplan/InteractiveFloorplan.json';
// import {  trackGAConversion } from '../../@utils/analytics';

import {
  InteractiveFloorplanStyles,
  OuterContainer,
  BlackoutDiv,
  InnerContainer,
} from './InteractiveFloorplan.styled';

const InteractiveFloorplan = () => {
  const [started, setStarted] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState(null);
  const [position, setPosition] = useState({ x: -1800, y: -1000 });

  const [smallScreen, setSmallScreen] = useState(false);
  const [isIpad, setIsIpad] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1800);
  const [windowHeight, setWindowHeight] = useState(1000);

  useWindowResize(() => {
    if (typeof window !== 'undefined' && typeof window.screen !== 'undefined') {
      let wwindowWidth =
        window.screen.width > window.innerWidth
          ? window.innerWidth
          : window.screen.width;
      let wwindowHeight =
        window.screen.height > window.innerHeight
          ? window.innerHeight
          : window.screen.height;

      setWindowWidth(wwindowWidth);
      setWindowHeight(wwindowHeight);
      setSmallScreen(wwindowWidth <= 970);

      // reposition as 'zoomed out'
      setPosition({
        x: wwindowWidth <= 970 ? -1100 : -1800,
        y: wwindowWidth <= 970 ? -600 : -1000,
      });
      setZoomed(false);
    }
  }, [970]);

  const browserChecks = () => {
    let isIpad = false;
    let isIphone = false;
    let windowWidth = 0;
    let windowHeight = 0;

    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      isIpad = /iPad/.test(navigator.platform) && !window.MSStream;
      isIphone = !!navigator.platform && /iPhone/.test(navigator.platform);
    }

    if (typeof window !== 'undefined' && typeof window.screen !== 'undefined') {
      windowWidth =
        window.screen.width > window.innerWidth
          ? window.innerWidth
          : window.screen.width;
      windowHeight =
        window.screen.height > window.innerHeight
          ? window.innerHeight
          : window.screen.height;
    }

    return { isIpad, isIphone, windowWidth, windowHeight };
  };

  useEffect(() => {
    const bc = browserChecks();

    setWindowWidth(bc.windowWidth);
    setWindowHeight(bc.windowHeight);
    setIsIpad(bc.isIpad);

    setSmallScreen(bc.windowWidth <= 970);
    setPosition({
      x:
        bc.smallScreen && typeof window !== 'undefined'
          ? bc.isIpad
            ? -1000
            : -1100
          : -1800,
      y:
        bc.smallScreen && typeof window !== 'undefined'
          ? bc.isIpad
            ? -525
            : -600
          : -1000,
    });

    if (bc.isIphone) {
      // solve iPhone opening at bottom of page
      let url = location.href;
      location.href = '#outer-container';
      history.replaceState(null, null, url);
    }
  }, []);

  const postTrackingEvent = (category, action, label) => {
    if (category && action && label) {
      //TODO: Add in Google Analytic events back in (Will be done with other integrations of GA)
      //trackGAConversion(category, action, label);
    }

    return true;
  };

  const onMouseDown = (e) => {
    // check for left mouse button
    if (e.button !== 0) return;

    e.stopPropagation();
    e.preventDefault();

    let posLeft = e.target.offsetLeft;
    let posTop = e.target.offsetTop;

    let { pageX } = e;
    let { pageY } = e;

    setDragging(true);
    setRel({
      x: pageX - posLeft,
      y: pageY - posTop,
    });
  };

  const onMouseMove = (e) => {
    if (e.target.tagName === 'SPAN') return;
    if (e.target.tagName === 'BUTTON') return;
    if (!dragging) return;

    e.stopPropagation();
    e.preventDefault();

    if (drawerExpanded) setDrawerExpanded(false);

    let { pageX } = e;
    let { pageY } = e;

    setPosition({
      x: pageX - rel.x,
      y: pageY - rel.y,
    });
  };

  const onMouseUp = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setDragging(false);
  };

  const onTouchStart = (e) => {
    if (typeof document !== 'undefined') {
      document.body.style.position = 'fixed';
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
    }

    let posLeft = e.target.offsetLeft;
    let posTop = e.target.offsetTop;

    let { pageX } = e.touches[0]; // eslint-disable-line prefer-destructuring
    let { pageY } = e.touches[0]; // eslint-disable-line prefer-destructuring

    setDragging(true);
    setRel({
      x: pageX - posLeft,
      y: pageY - posTop,
    });
  };

  const onTouchMove = (e) => {
    let { pageX } = e.touches[0]; // eslint-disable-line prefer-destructuring
    let { pageY } = e.touches[0]; // eslint-disable-line prefer-destructuring

    setPosition({
      x: pageX - rel.x,
      y: pageY - rel.y,
    });
  };

  const onTouchEnd = () => {
    setDragging(false);

    if (typeof document !== 'undefined') {
      setTimeout(() => {
        document.body.style.position = '';
        document.body.style.overflow = '';
        document.body.style.width = '';
      }, 300); // brief delay fixes momemtum scrolling quirk
    }
  };

  const zoomIn = () => {
    setStarted(true);
    setZoomed(true);
  };

  const zoomOut = () => {
    setPosition({
      x: smallScreen ? -1100 : -1800,
      y: smallScreen ? -600 : -1000,
    });
    setZoomed(false);
  };

  const toggleDrawer = () => {
    if (typeof document !== 'undefined') {
      document.getElementById('ifp-drawer').scrollTop = 0;
    }

    setDrawerExpanded(!drawerExpanded);
  };

  const navigateNext = () => {
    const dotCount = data.length - 1;

    let newIndex = selectedIndex + 1;
    const newData = data[newIndex];

    if (selectedIndex >= dotCount) newIndex = 0;

    setSelectedIndex(newIndex);

    if (newData) {
      postTrackingEvent(
        'interactive floorplan',
        'next click',
        newData.sidebar.title
      );
    }
  };

  const navigatePrev = () => {
    const dotCount = data.length - 1;

    let newIndex = selectedIndex - 1;
    const newData = data[newIndex];

    if (selectedIndex <= 0) newIndex = dotCount;

    setSelectedIndex(newIndex);

    if (newData) {
      postTrackingEvent(
        'interactive floorplan',
        'previous click',
        newData.sidebar.title
      );
    }
  };

  const handleDotClick = (item, index) => {
    postTrackingEvent('interactive floorplan', 'pin click', item.sidebar.title);

    setStarted(true);
    setSelectedIndex(index);
  };

  const calculateDotPosition = () => {
    let targetBounds = { left: windowWidth / 2, top: windowHeight / 2 };
    if (typeof document !== 'undefined') {
      let elem = document.getElementById('target-' + selectedIndex);
      targetBounds = elem
        ? elem.getBoundingClientRect()
        : { left: windowWidth / 2, top: windowHeight / 2 };
    }

    const leftCompensation = 180; // adjustment for left content area (desktop sidebar)
    const bottomCompensation = 80; // adjustment for lower content area (mobile 'sidebar')

    let xAdjustment;
    let yAdjustment;

    if (smallScreen) {
      xAdjustment = targetBounds.left - windowWidth / 2;
      yAdjustment = targetBounds.top - windowHeight / 2 + bottomCompensation;
    } else {
      xAdjustment = targetBounds.left - windowWidth / 2 - leftCompensation;
      yAdjustment = targetBounds.top - windowHeight / 2;
    }

    if (isIpad) {
      setPosition({
        x: position.x,
        y: position.y,
      });
    } else {
      setPosition({
        x: position.x - xAdjustment,
        y: position.y - yAdjustment,
      });
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const floorplan = document.getElementById('ifp-floorplan');
      if (floorplan) {
        floorplan.addEventListener('mousemove', onMouseMove);
        floorplan.addEventListener('mouseup', onMouseUp);
      }

      return () => {
        if (typeof document !== 'undefined') {
          const floorplan = document.getElementById('ifp-floorplan');
          if (floorplan) {
            floorplan.removeEventListener('mousemove', onMouseMove);
            floorplan.removeEventListener('mouseup', onMouseUp);
          }
        }
      };
    }
  }); // Note: I don't want the empty array on the effect.

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setDrawerExpanded(false);
      calculateDotPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const selectedData = data[selectedIndex];

  return (
    <InteractiveFloorplanStyles>
      <Head>
        <title>Interactive Floor Plan | Clayton Homes</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no"
        />
        <meta name="referrer" content="always" />
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      </Head>
      {drawerExpanded ? (
        <BlackoutDiv className={drawerExpanded ? 'open' : ''} />
      ) : null}
      <OuterContainer id="outer-container">
        <Sidebar
          toggleDrawer={toggleDrawer}
          navigateNext={navigateNext}
          navigatePrev={navigatePrev}
          data={selectedData ? selectedData.sidebar : null}
          started={started}
          postTrackingEvent={postTrackingEvent}
        />

        <Drawer
          drawerExpanded={drawerExpanded}
          toggleDrawer={toggleDrawer}
          data={selectedData ? selectedData.drawer : null}
        />

        <InnerContainer>
          <ZoomControls zoomIn={zoomIn} zoomOut={zoomOut} zoomed={zoomed} />

          <Button
            isCompact={true}
            nextJSUrl="/locations/"
            linkComponent={Link}
            onButtonClick={() =>
              postTrackingEvent(
                'interactive floorplan',
                'cta click',
                'find a home center'
              )
            }
            className="mobile-cta"
            automationId="ifp-cta-button"
          >
            Find a Home Center
          </Button>

          <Floorplan
            dragging={dragging}
            zoomed={zoomed}
            position={position}
            data={data}
            selectedIndex={selectedIndex}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={(e) => onMouseDown(e)}
            handleDotClick={handleDotClick}
            smallScreen={smallScreen}
          />
        </InnerContainer>
      </OuterContainer>
    </InteractiveFloorplanStyles>
  );
};

export default InteractiveFloorplan;
